import { COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS } from '@core/components/component-factory/component-factory-config-input.registry';
import type { ConfigComponentContract } from '@shared/utils/content-builder/config-component-contract';
import { computedResolvedConfig } from '@shared/utils/content-builder/block-config-computed-resolver';
import type { KioskProductAddPageConfig } from '@front-platform/content-builder';
import { kioskProductAddPageResolveConfig } from '@front-platform/content-builder';
import { Location } from '@angular/common';
import { Component, Inject, computed, input, OnInit, signal } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { ProductStoreDispatcher } from '@common/dispatchers/product.dispatcher';
import { ShopStoreDispatcher } from '@common/dispatchers/shop.dispatcher';
import { ProductStoreSelector } from '@common/selectors/product.selector';
import { ICartItem } from '@core/models/cart.model';
import {
  IContentBuilderFieldColor,
  IContentBuilderFieldImage,
} from '@core/models/content-builder-fields.model';
import type { DoodProductModel } from '@core/models/product.model';
import type { DoodShopModel } from '@core/models/shop.model';
import { CartService } from '@core/services/cart/cart.service';
import { Cb2PreviewStateService } from '@core/services/cb2-preview/cb2-preview-state.service';
import { ProductService } from '@core/services/product/product.service';
import { RouterHelperService } from '@core/services/router-helper/router-helper.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  KioskProductAddShellAddToCartPayload,
  KioskProductAddShellComponent,
} from '@shared/pages/kiosk/kiosk-product-add-shell/kiosk-product-add-shell.component';
import { filter, map, take, tap } from 'rxjs';
import {
  KIOSK_PRODUCT_ADD_PAGE_DATA_SOURCE,
  KioskProductAddPageDataSource,
} from './kiosk-product-add-page-view-model';
import { provideKioskProductAddPageDataSource } from './kiosk-product-add-page-data-source.providers';
export interface KioskProductAddPageRuntimeConfig {}

export type KioskProductAddPageConfigInput = Partial<
  Omit<KioskProductAddPageConfig, keyof KioskProductAddPageRuntimeConfig> &
    KioskProductAddPageRuntimeConfig
>;
export type KioskProductAddPageResolvedConfig = Omit<
  KioskProductAddPageConfig,
  keyof KioskProductAddPageRuntimeConfig
> &
  Partial<KioskProductAddPageRuntimeConfig>;
export interface KioskProductAddPageContract extends ConfigComponentContract<
  KioskProductAddPageConfigInput,
  KioskProductAddPageResolvedConfig
> {}

@Component({
  selector: 'app-kiosk-product-add-page',
  template: `
    @if (routeProductId() && activeShopId()) {
      <app-kiosk-product-add-shell
        [productId]="routeProductId()!"
        [shopId]="activeShopId()!"
        [backgroundColor]="backgroundColor()"
        [backgroundImage]="backgroundImage()"
        [secondaryColor]="secondaryColor()"
        [textColor]="textColor()"
        [buttonBackgroundColor]="buttonBackgroundColor()"
        [buttonTextColor]="buttonTextColor()"
        [unavailableButtonBackground]="unavailableButtonBackground()"
        [unavailableButtonText]="unavailableButtonText()"
        [cardColor]="cardColor()"
        [compositionBackgroundColor]="compositionBackgroundColor()"
        (addToCartRequest)="onShellAddToCart($event)"
        (backRequest)="navigateBackToCategory()"
      />
    }
  `,
  imports: [KioskProductAddShellComponent],
  providers: [...provideKioskProductAddPageDataSource()],
})
export class KioskProductAddPageComponent implements OnInit, KioskProductAddPageContract {
  readonly config = input<KioskProductAddPageConfigInput>({});
  readonly resolvedConfig = computedResolvedConfig<
    KioskProductAddPageConfig,
    KioskProductAddPageRuntimeConfig
  >(kioskProductAddPageResolveConfig, () => ({ config: this.config() }));
  readonly backgroundColor = computed(
    () => this.resolvedConfig()['background_color'] as unknown as IContentBuilderFieldColor,
  );
  readonly backgroundImage = computed(
    () =>
      this.resolvedConfig()['background_image'] as unknown as IContentBuilderFieldImage | undefined,
  );
  readonly secondaryColor = computed(
    () => this.resolvedConfig()['secondary_color'] as unknown as IContentBuilderFieldColor,
  );
  readonly textColor = computed(
    () => this.resolvedConfig()['text_color'] as unknown as IContentBuilderFieldColor,
  );
  readonly buttonBackgroundColor = computed(
    () => this.resolvedConfig()['button_background_color'] as unknown as IContentBuilderFieldColor,
  );
  readonly buttonTextColor = computed(
    () => this.resolvedConfig()['button_text_color'] as unknown as IContentBuilderFieldColor,
  );
  readonly unavailableButtonBackground = computed(
    () =>
      this.resolvedConfig()[
        'unavailable_button_background'
      ] as unknown as IContentBuilderFieldColor,
  );
  readonly unavailableButtonText = computed(
    () => this.resolvedConfig()['unavailable_button_text'] as unknown as IContentBuilderFieldColor,
  );
  readonly cardColor = computed(
    () => this.resolvedConfig()['card_color'] as unknown as IContentBuilderFieldColor,
  );
  readonly compositionBackgroundColor = computed(
    () =>
      this.resolvedConfig()['composition_background_color'] as unknown as IContentBuilderFieldColor,
  );

  protected readonly routeProductId = signal<string | null>(null);
  protected readonly activeShopId = signal<string | null>(null);

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly cartService: CartService,
    private readonly location: Location,
    private readonly productSelector: ProductStoreSelector,
    private readonly routerHelperService: RouterHelperService,
    private readonly previewState: Cb2PreviewStateService,
    private readonly productService: ProductService,
    private readonly productDispatcher: ProductStoreDispatcher,
    private readonly shopDispatcher: ShopStoreDispatcher,
    @Inject(KIOSK_PRODUCT_ADD_PAGE_DATA_SOURCE)
    private readonly blockDataSource: KioskProductAddPageDataSource,
  ) {
    if (this.previewState.isActive()) {
      this.blockDataSource.vm$.pipe(takeUntilDestroyed()).subscribe(vm => {
        if (!vm) {
          return;
        }

        this.routeProductId.set(vm.productId);
        this.activeShopId.set(vm.shopId);
        this.productDispatcher.upsertMany(vm.shop.products as unknown as DoodProductModel[]);
        this.shopDispatcher.insertActive(vm.shop as unknown as DoodShopModel);
        this.cartService.updateShopIdForActiveCart(vm.shopId);
        this.productService.setProductToStore(vm.product);
      });
      return;
    }

    this.updateRouteProductId();

    this.route.params.pipe(take(1)).subscribe(() => this.updateRouteProductId());

    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        tap(() => this.updateRouteProductId()),
        takeUntilDestroyed(),
      )
      .subscribe();
  }

  ngOnInit(): void {
    if (this.previewState.isActive()) {
      return;
    }

    this.activeShopId.set(this.cartService.getShopId() ?? null);
  }

  private updateRouteProductId(): void {
    this.routeProductId.set(this.getProductIdFromRoute(this.route));
  }

  private getProductIdFromRoute(route: ActivatedRoute): string | null {
    while (route.firstChild) {
      route = route.firstChild;
    }
    return route.snapshot.paramMap.get('productId');
  }

  onShellAddToCart(payload: KioskProductAddShellAddToCartPayload): void {
    if (!payload.validity.isValid) {
      return;
    }
    const newCartItem = payload.cartDraft as unknown as Omit<ICartItem, 'id'>;
    this.cartService
      .addOrUpdateCartItem$({
        newCartItem,
        quantity: payload.quantity,
        product: payload.product,
      })
      .subscribe(() => this.location.back());
  }

  navigateBackToCategory(): void {
    const shopCity = this.route.snapshot.paramMap.get('shopCity');
    const shopType = this.route.snapshot.paramMap.get('shopType');
    const shopSlug = this.route.snapshot.paramMap.get('shopSlug');

    this.productSelector.selectActiveCategory
      .pipe(
        map(id => {
          this.router.navigate([
            this.routerHelperService.translateRoute(
              `${shopCity}/${shopType}/${shopSlug}${id ? `/categories/${id}` : ''}`,
            ),
          ]);
        }),
      )
      .subscribe();
  }
}
COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS.set(KioskProductAddPageComponent, { 'keyAliases': {} });
